import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { TopicDto } from "../types";

interface TopicsTreePanelProps {
    isLoading: boolean;
    nodes: TopicDto[];
    selectedTopicId: number | null;
    onSelectTopic: (id: number) => void;
    getNodeName: (node: TopicDto) => string;
    searchText: string;
    setSearchText: (searchText: string) => void;
}

const getAllNodeIds = (treeNodes: TopicDto[]): number[] =>
    treeNodes.flatMap((node) => [node.id, ...getAllNodeIds(node.childrens || [])]);

const filterTree = (treeNodes: TopicDto[], search: string, getNodeName: (node: TopicDto) => string): TopicDto[] => {
    if (!search.trim()) return treeNodes;

    const normalized = search.trim().toLowerCase();
    return treeNodes
        .map((node) => {
            const children = filterTree(node.childrens || [], normalized, getNodeName);
            const ownName = getNodeName(node).toLowerCase();
            if (ownName.includes(normalized) || children.length > 0) {
                return { ...node, childrens: children };
            }
            return null;
        })
        .filter((node): node is TopicDto => node !== null);
};

export default function TopicsTreePanel({
    isLoading,
    nodes,
    selectedTopicId,
    onSelectTopic,
    getNodeName,
    searchText,
    setSearchText
}: TopicsTreePanelProps) {
    const { t } = useTranslation("topic");
    const [showExpandAll, setShowExpandAll] = useState(true);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        setExpandedIds(new Set());
        setShowExpandAll(true);
    }, [nodes]);

    const visibleTree = useMemo(
        () => filterTree(nodes, searchText, getNodeName),
        [nodes, searchText, getNodeName]
    );

    const handleToggleNode = (id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleToggleExpandAll = () => {
        if (showExpandAll) {
            setExpandedIds(new Set(getAllNodeIds(nodes)));
            setShowExpandAll(false);
        } else {
            setExpandedIds(new Set());
            setShowExpandAll(true);
        }
    };

    const renderTree = (treeNodes: TopicDto[], level = 0): ReactNode =>
        treeNodes.map((node) => {
            const hasChildren = (node.childrens || []).length > 0;
            const isExpanded = expandedIds.has(node.id);
            const isSelected = selectedTopicId === node.id;

            return (
                <div key={node.id}>
                    <div
                        className={`topics-management-tree-row ${isSelected ? "selected" : ""}`}
                        style={{ paddingLeft: `${level * 24}px` }}
                        onClick={() => onSelectTopic(node.id)}
                    >
                        <button
                            type="button"
                            className="topics-management-tree-expand"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) handleToggleNode(node.id);
                            }}
                        >
                            {hasChildren ? (isExpanded ? "-" : "+") : ""}
                        </button>
                        <span className="topics-management-tree-label">{getNodeName(node)}</span>
                    </div>
                    {hasChildren && isExpanded && renderTree(node.childrens, level + 1)}
                </div>
            );
        });

    return (
        <>
            <Form.Control
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t("searchPlaceholder")}
            />

            <div className="border p-3 topics-management-panel mt-3">
                <div
                    className="topics-management-tree-toggle"
                    onClick={handleToggleExpandAll}
                >
                    [{showExpandAll ? "+" : "-"}] {showExpandAll ? t("expandAll") : t("collapseAll")}
                </div>

                {isLoading ? (
                    <div className="topics-management-loading">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <div>{renderTree(visibleTree)}</div>
                )}
            </div>
        </>
    );
}
